import { useEffect, useMemo, useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import {
  listProductCategories,
  listProductLines,
  ProductCategory,
  ProductLine,
  ProductSearchResult,
  searchProducts,
  updateProduct,
} from "@/api/product_catalog"
import { FileText, Loader2, Save, Upload, X } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

interface ProductCatalogImportResponse {
  created_count: number
  updated_count: number
  skipped_count: number
  pricing_count?: number
  error_count: number
  errors: string[]
}

interface ProductDraftState {
  name: string
  description: string
  sku: string
  lifecycle_stage: string
  is_pack: boolean
  is_sellable: boolean
  units_per_case: string
  unit_size: string
  default_uom: string
  product_line_id: string
  product_category_id: string
  product_group_ids: string[]
  identifiers_text: string
  aliases_text: string
}

interface FormTextInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
}

interface FormSelectOption {
  value: string
  label: string
}

interface FormSelectProps {
  label: string
  value: string
  options: FormSelectOption[]
  onChange: (value: string) => void
}

interface CheckboxInputProps {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

interface FormTextareaProps {
  label: string
  value: string
  onChange: (value: string) => void
}

function getProductSubtitle(product: ProductSearchResult): string {
  return [product.product_line, product.product_category, product.unit_size, product.identifiers[0]].filter(Boolean).join(" / ")
}

function getSelectedFileLabel(file: File | null): string {
  return file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "No CSV selected"
}

function parseDelimitedRows(value: string): string[] {
  return value
    .split(/\n|,/g)
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

function normalizeOptionText(value: string): string {
  return value.trim().toLowerCase()
}

function buildProductDraft(product: ProductSearchResult): ProductDraftState {
  return {
    name: product.name,
    description: "",
    sku: product.sku || "",
    lifecycle_stage: product.lifecycle_stage || "",
    is_pack: product.is_pack,
    is_sellable: product.is_sellable,
    units_per_case: product.units_per_case?.toString() || "",
    unit_size: product.unit_size || "",
    default_uom: "",
    product_line_id: product.product_line_id || "",
    product_category_id: product.product_category_id || "",
    product_group_ids: [...new Set(product.product_groups.map((group) => normalizeOptionText(group)))],
    identifiers_text: product.identifiers.join("\n"),
    aliases_text: product.aliases.join("\n"),
  }
}

export function ProductCatalogSettings() {
  const [query, setQuery] = useState("")
  const [products, setProducts] = useState<ProductSearchResult[]>([])
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [lines, setLines] = useState<ProductLine[]>([])
  const [selectedProductId, setSelectedProductId] = useState("")
  const [draft, setDraft] = useState<ProductDraftState | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedGroupsFile, setSelectedGroupsFile] = useState<File | null>(null)
  const [selectedPricingFile, setSelectedPricingFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [lastResult, setLastResult] = useState<ProductCatalogImportResponse | null>(null)

  const selectedProduct = useMemo(
    () => products.find((product) => product.id === selectedProductId) || null,
    [products, selectedProductId]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDropFile,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      "text/csv": [".csv"],
    },
  })

  useEffect(() => {
    void loadCatalog()
  }, [])

  useEffect(() => {
    void loadProducts()
  }, [query])

  useEffect(() => {
    if (selectedProduct && !draft) {
      setDraft(buildProductDraft(selectedProduct))
    }
  }, [selectedProduct?.id])

  function handleDropFile(files: File[]) {
    setSelectedFile(files[0] || null)
  }

  function handleDropGroupsFile(files: File[]) {
    setSelectedGroupsFile(files[0] || null)
  }

  function handleDropPricingFile(files: File[]) {
    setSelectedPricingFile(files[0] || null)
  }

  function handleClearSelectedFile() {
    setSelectedFile(null)
  }

  function handleClearSelectedGroupsFile() {
    setSelectedGroupsFile(null)
  }

  function handleClearSelectedPricingFile() {
    setSelectedPricingFile(null)
  }

  async function loadCatalog() {
    setLoading(true)
    try {
      const [categoriesResponse, linesResponse] = await Promise.all([listProductCategories(), listProductLines()])
      if (categoriesResponse.ok) setCategories(categoriesResponse.value.data)
      if (linesResponse.ok) setLines(linesResponse.value.data)
    } finally {
      setLoading(false)
    }
  }

  async function loadProducts() {
    const response = await searchProducts(query, 100)
    if (!response.ok) return
    setProducts(response.value.data)
    if (!selectedProductId && response.value.data.length > 0) {
      const first = response.value.data[0]
      setSelectedProductId(first.id)
      setDraft(buildProductDraft(first))
    }
  }

  function handleSelectProduct(product: ProductSearchResult) {
    setSelectedProductId(product.id)
    setDraft(buildProductDraft(product))
  }

  function updateDraft(patch: Partial<ProductDraftState>) {
    setDraft((current) => (current ? { ...current, ...patch } : current))
  }

  function toggleGroupSelection(groupName: string) {
    if (!draft) return
    const normalized = normalizeOptionText(groupName)
    const exists = draft.product_group_ids.includes(normalized)
    const next = exists
      ? draft.product_group_ids.filter((group) => group !== normalized)
      : [...draft.product_group_ids, normalized]
    updateDraft({ product_group_ids: next })
  }

  async function handleSaveProduct() {
    if (!selectedProduct || !draft) return
    if (!draft.name.trim()) {
      toast({ title: "Name is required", description: "Provide a product name before saving.", variant: "destructive" })
      return
    }

    setSaving(true)
    try {
      const selectedGroupIds = categories
        .filter((category) => draft.product_group_ids.includes(normalizeOptionText(category.name)))
        .map((category) => category.id)

      const response = await updateProduct(selectedProduct.id, {
        name: draft.name.trim(),
        description: draft.description || undefined,
        sku: draft.sku || undefined,
        lifecycle_stage: draft.lifecycle_stage || undefined,
        is_pack: draft.is_pack,
        is_sellable: draft.is_sellable,
        units_per_case: draft.units_per_case ? Number(draft.units_per_case) : undefined,
        unit_size: draft.unit_size || undefined,
        default_uom: draft.default_uom || undefined,
        product_line_id: draft.product_line_id || undefined,
        product_category_id: draft.product_category_id || undefined,
        product_group_ids: selectedGroupIds,
        identifiers: parseDelimitedRows(draft.identifiers_text),
        aliases: parseDelimitedRows(draft.aliases_text),
      })

      if (!response.ok) {
        toast({ title: "Failed to save product", description: response.error.message, variant: "destructive" })
        return
      }

      const updated = response.value.data
      setProducts((currentProducts) => currentProducts.map((product) => (product.id === updated.id ? updated : product)))
      setDraft(buildProductDraft(updated))
      toast({ title: "Product updated", description: `${updated.name} was saved.` })
    } finally {
      setSaving(false)
    }
  }

  async function handleUpload() {
    if (!selectedFile) {
      toast({ title: "No file selected", description: "Choose a CSV file to upload.", variant: "destructive" })
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("products_file", selectedFile)
      if (selectedGroupsFile) {
        formData.append("groups_file", selectedGroupsFile)
      }
      if (selectedPricingFile) {
        formData.append("pricing_file", selectedPricingFile)
      }
      const response = await fetch("/api/product-catalog/import", {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      })
      if (!response.ok) throw new Error(await response.text())

      const result = await response.json()
      const payload: ProductCatalogImportResponse = result.data || result
      setLastResult(payload)
      toast({
        title: "Product catalog import complete",
        description: `Created ${payload.created_count}, updated ${payload.updated_count}, prices ${payload.pricing_count || 0}, skipped ${payload.skipped_count}, errors ${payload.error_count}`,
        variant: payload.error_count > 0 ? "destructive" : "default",
      })
      await Promise.all([loadProducts(), loadCatalog()])
      setSelectedFile(null)
      setSelectedGroupsFile(null)
      setSelectedPricingFile(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to upload product catalog CSV"
      toast({ title: "Upload failed", description: message, variant: "destructive" })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div class="space-y-5">
      <div class="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p class="font-medium">Catalog CSV import</p>
        <p class="mt-1 text-xs">
          Supports Vividly and Confido product exports. For Confido, upload products as the main CSV and pricing as the
          optional pricing CSV.
        </p>
      </div>

      <div class="space-y-4 rounded-md border border-gray-200 p-4">
        <div
          {...(getRootProps() as object)}
          class={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
            isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
          <p class="text-sm font-medium text-gray-900 mb-1">
            {isDragActive ? "Drop product catalog CSV here" : "Drag and drop product catalog CSV"}
          </p>
          <p class="text-xs text-gray-500 mb-3">Only `.csv` files are supported</p>
          <Button variant="outline" onClick={open} disabled={uploading}>
            Select CSV
          </Button>
        </div>

        <div class="flex items-center justify-between rounded-md border border-gray-200 p-3">
          <div class="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-gray-500" />
            <span>{getSelectedFileLabel(selectedFile)}</span>
          </div>
          <div class="flex items-center gap-2">
            <Button variant="outline" onClick={handleClearSelectedFile} disabled={!selectedFile || uploading}>
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
            <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
              {uploading ? "Uploading..." : "Import Catalog CSV"}
            </Button>
          </div>
        </div>

        <GroupsFileUploadRow
          selectedGroupsFile={selectedGroupsFile}
          uploading={uploading}
          onDrop={handleDropGroupsFile}
          onClear={handleClearSelectedGroupsFile}
        />
        <OptionalFileUploadRow
          title="Optional: Pricing Export CSV"
          emptyLabel="No pricing CSV selected"
          buttonLabel="Select Pricing CSV"
          dropLabel="Drag and drop pricing CSV"
          activeDropLabel="Drop pricing CSV here"
          selectedFile={selectedPricingFile}
          uploading={uploading}
          onDrop={handleDropPricingFile}
          onClear={handleClearSelectedPricingFile}
        />
      </div>

      {lastResult && (
        <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
          <div>Created: {lastResult.created_count}</div>
          <div>Updated: {lastResult.updated_count}</div>
          <div>Prices: {lastResult.pricing_count || 0}</div>
          <div>Skipped: {lastResult.skipped_count}</div>
          <div>Errors: {lastResult.error_count}</div>
          {lastResult.errors.length > 0 && (
            <div class="mt-2 max-h-32 overflow-y-auto text-xs text-red-700">
              {lastResult.errors.slice(0, 10).map((error) => (
                <div key={error}>{error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div class="flex items-center justify-between gap-3">
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700">Search products</label>
          <input
            value={query}
            onInput={(event) => setQuery(event.currentTarget.value)}
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search by name, UPC, product code, line, or group"
          />
        </div>
        <Button variant="outline" onClick={() => void Promise.all([loadProducts(), loadCatalog()])} disabled={loading || saving}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Refreshing...
            </>
          ) : (
            "Refresh"
          )}
        </Button>
      </div>

      <div class="grid w-full grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div class="min-h-[40rem] max-h-[calc(100vh-9rem)] overflow-y-auto rounded-md border border-gray-200">
          {products.length === 0 ? (
            <div class="p-3 text-sm text-gray-500">No catalog products found.</div>
          ) : (
            <Table class="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead class="px-3">Product</TableHead>
                  <TableHead class="px-3">Line/Group</TableHead>
                  <TableHead class="px-3">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow
                    key={product.id}
                    class={`cursor-pointer ${selectedProductId === product.id ? "bg-blue-50" : ""}`}
                    onClick={() => handleSelectProduct(product)}
                  >
                    <TableCell class="px-3 py-2">
                      <div class="font-medium text-gray-900">{product.name}</div>
                      <div class="text-xs text-gray-500">{product.sku || product.identifiers[0] || "No SKU/identifier"}</div>
                    </TableCell>
                    <TableCell class="px-3 py-2 text-xs text-gray-600">
                      {product.product_line && <div>{product.product_line}</div>}
                      <div>{product.product_category || "No primary group"}</div>
                    </TableCell>
                    <TableCell class="px-3 py-2 text-xs">
                      <div class="flex flex-wrap gap-1">
                        {product.is_sellable && <span class="rounded bg-green-50 px-2 py-0.5 text-green-700">Sellable</span>}
                        {product.is_pack && <span class="rounded bg-purple-50 px-2 py-0.5 text-purple-700">Pack</span>}
                        {product.lifecycle_stage && <span class="rounded bg-gray-100 px-2 py-0.5 text-gray-700">{product.lifecycle_stage}</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {selectedProduct && draft ? (
          <div class="min-h-[40rem] max-h-[calc(100vh-9rem)] space-y-4 overflow-y-auto rounded-md border border-gray-200 p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h4 class="text-base font-semibold text-gray-900">Edit Product</h4>
                <p class="text-xs text-gray-500">{getProductSubtitle(selectedProduct) || "Update product metadata and mappings."}</p>
              </div>
              <Button onClick={handleSaveProduct} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormTextInput label="Product Name" value={draft.name} onChange={(value) => updateDraft({ name: value })} required />
              <FormTextInput label="SKU" value={draft.sku} onChange={(value) => updateDraft({ sku: value })} />
              <FormTextInput label="Lifecycle Stage" value={draft.lifecycle_stage} onChange={(value) => updateDraft({ lifecycle_stage: value })} />
              <FormTextInput label="Unit Size" value={draft.unit_size} onChange={(value) => updateDraft({ unit_size: value })} />
              <FormTextInput label="Units Per Case" value={draft.units_per_case} onChange={(value) => updateDraft({ units_per_case: value })} />
              <FormTextInput label="Default UOM" value={draft.default_uom} onChange={(value) => updateDraft({ default_uom: value })} />
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormSelect
                label="Product Line"
                value={draft.product_line_id}
                options={[{ value: "", label: "None" }, ...lines.map((line) => ({ value: line.id, label: line.name }))]}
                onChange={(value) => updateDraft({ product_line_id: value })}
              />
              <FormSelect
                label="Primary Product Group"
                value={draft.product_category_id}
                options={[{ value: "", label: "None" }, ...categories.map((category) => ({ value: category.id, label: category.name }))]}
                onChange={(value) => updateDraft({ product_category_id: value })}
              />
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <CheckboxInput label="Is Pack" checked={draft.is_pack} onChange={(checked) => updateDraft({ is_pack: checked })} />
              <CheckboxInput label="Is Sellable" checked={draft.is_sellable} onChange={(checked) => updateDraft({ is_sellable: checked })} />
            </div>

            <textarea
              value={draft.description}
              onInput={(event) => updateDraft({ description: event.currentTarget.value })}
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Description"
            />

            <div>
              <p class="text-sm font-medium text-gray-700 mb-2">Product Groups (multi-select)</p>
              <div class="max-h-40 overflow-y-auto rounded-md border border-gray-200 p-2">
                {categories.map((category) => {
                  const normalized = normalizeOptionText(category.name)
                  return (
                    <label key={category.id} class="flex items-center gap-2 px-1 py-1 text-sm">
                      <input
                        type="checkbox"
                        checked={draft.product_group_ids.includes(normalized)}
                        onChange={() => toggleGroupSelection(category.name)}
                      />
                      <span>{category.name}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormTextarea
                label="Identifiers (one per line)"
                value={draft.identifiers_text}
                onChange={(value) => updateDraft({ identifiers_text: value })}
              />
              <FormTextarea
                label="Aliases (one per line)"
                value={draft.aliases_text}
                onChange={(value) => updateDraft({ aliases_text: value })}
              />
            </div>

            <div>
              <p class="text-sm font-medium text-gray-700 mb-2">Customer Pricing (view all)</p>
              <div class="max-h-48 overflow-y-auto rounded-md border border-gray-200">
                {selectedProduct.prices.length === 0 ? (
                  <div class="p-3 text-xs text-gray-500">No customer prices stored.</div>
                ) : (
                  selectedProduct.prices.map((price) => (
                    <div key={`${selectedProduct.id}-${price.customer_name}-${price.unit_price}`} class="border-b border-gray-100 p-2 text-xs last:border-b-0">
                      <div class="font-medium text-gray-700">{price.customer_name}</div>
                      <div class="text-gray-600">
                        {price.currency} {price.unit_price.toFixed(2)}
                        {price.retailer_name ? ` • ${price.retailer_name}` : ""}
                      </div>
                      <div class="text-gray-500">
                        {price.effective_start || "Any start"} - {price.effective_end || "Any end"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <div class="rounded-md border border-gray-200 p-4 text-sm text-gray-500">
            Select a product to view and edit full metadata.
          </div>
        )}
      </div>
    </div>
  )
}

function FormTextInput({ label, value, onChange, required = false }: FormTextInputProps) {
  return (
    <label class="block">
      <span class="mb-1 block text-sm font-medium text-gray-700">
        {label} {required && <span class="text-red-500">*</span>}
      </span>
      <input
        value={value}
        onInput={(event) => onChange(event.currentTarget.value)}
        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  )
}

function FormSelect({ label, value, options, onChange }: FormSelectProps) {
  return (
    <label class="block">
      <span class="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {options.map((option) => (
          <option key={`${label}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function CheckboxInput({ label, checked, onChange }: CheckboxInputProps) {
  return (
    <label class="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.currentTarget.checked)} />
      <span>{label}</span>
    </label>
  )
}

function FormTextarea({ label, value, onChange }: FormTextareaProps) {
  return (
    <label class="block">
      <span class="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <textarea
        value={value}
        onInput={(event) => onChange(event.currentTarget.value)}
        rows={5}
        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  )
}

interface GroupsFileUploadRowProps {
  selectedGroupsFile: File | null
  uploading: boolean
  onDrop: (files: File[]) => void
  onClear: () => void
}

function GroupsFileUploadRow({ selectedGroupsFile, uploading, onDrop, onClear }: GroupsFileUploadRowProps) {
  return (
    <OptionalFileUploadRow
      title="Optional: Product Groups Export CSV"
      emptyLabel="No product groups CSV selected"
      buttonLabel="Select Product Groups CSV"
      dropLabel="Drag and drop product groups CSV"
      activeDropLabel="Drop product groups CSV here"
      selectedFile={selectedGroupsFile}
      uploading={uploading}
      onDrop={onDrop}
      onClear={onClear}
    />
  )
}

interface OptionalFileUploadRowProps {
  title: string
  emptyLabel: string
  buttonLabel: string
  dropLabel: string
  activeDropLabel: string
  selectedFile: File | null
  uploading: boolean
  onDrop: (files: File[]) => void
  onClear: () => void
}

function OptionalFileUploadRow({
  title,
  emptyLabel,
  buttonLabel,
  dropLabel,
  activeDropLabel,
  selectedFile,
  uploading,
  onDrop,
  onClear,
}: OptionalFileUploadRowProps) {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      "text/csv": [".csv"],
    },
  })

  return (
    <div class="space-y-2 rounded-md border border-gray-200 p-3">
      <p class="text-xs font-medium text-gray-700">{title}</p>
      <div
        {...(getRootProps() as object)}
        class={`border border-dashed rounded-md p-3 text-center cursor-pointer ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <input {...getInputProps()} />
        <p class="text-xs text-gray-600">{isDragActive ? activeDropLabel : dropLabel}</p>
        <Button variant="outline" onClick={open} disabled={uploading} class="mt-2">
          {buttonLabel}
        </Button>
      </div>
      <div class="flex items-center justify-between rounded-md border border-gray-200 p-2 text-sm">
        <span>{selectedFile ? getSelectedFileLabel(selectedFile) : emptyLabel}</span>
        <Button variant="outline" onClick={onClear} disabled={!selectedFile || uploading}>
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("parseDelimitedRows", () => {
    it("splits newline and comma separated values", () => {
      expect(parseDelimitedRows("A\nB, C")).toEqual(["A", "B", "C"])
    })
  })

  describe("buildProductDraft", () => {
    it("normalizes product groups to lowercase keys", () => {
      const draft = buildProductDraft({
        id: "p1",
        name: "Classic Alfredo Sauce",
        is_pack: true,
        is_sellable: true,
        product_groups: ["All Products", "15oz Jars"],
        identifiers: [],
        aliases: [],
        prices: [],
      } as ProductSearchResult)
      expect(draft.product_group_ids).toEqual(["all products", "15oz jars"])
    })
  })
}
